import { CreditTypeEnum } from "@/__generated__/graphql";
import { CreditAccountOverviewScreen } from "./CreditAccountOverview.screen";
import { useAuthState } from "@/store/auth";
import type { CreditAccountType } from "@/types/profile";
import { useClientCreditAccounts } from "@/gqlhooks/profile/useClientCreditAccounts";

export type CreditAccountOverviewProps = {
  overviewBalance: string;
  generalBalance: string;
  creditAccounts: {
    type: Exclude<CreditAccountType, "overview" | "general">;
    balance: string;
  }[];
};

export const CreditAccountOverview = () => {
  const userInfoId = useAuthState((user) => user.data.userInfo.id);
  const { data } = useClientCreditAccounts(userInfoId);
  const clientCreditAccount = data?.client.creditAccounts ?? [];
  const departmentLinkedNameByType = {
    massage: "MASSAGE",
    beauty: "HOME_BEAUTY",
    pet: "PET_CARE",
  } as const;

  const overviewBalance = clientCreditAccount
    .filter(
      (credit) =>
        credit.creditType === CreditTypeEnum.Department ||
        credit.creditType === CreditTypeEnum.General,
    )
    .reduce((sum, credit) => sum + (credit.balance ?? 0), 0);

  const getBalance = (creditType: CreditTypeEnum) =>
    String(
      clientCreditAccount.find((c) => c.creditType === creditType)?.balance ??
        0,
    );

  const getDepartmentBalance = (
    type: keyof typeof departmentLinkedNameByType,
  ) =>
    String(
      clientCreditAccount.find(
        (credit) =>
          credit.creditType === CreditTypeEnum.Department &&
          credit.linkedObject?.__typename === "Department" &&
          credit.linkedObject.name === departmentLinkedNameByType[type],
      )?.balance ?? 0,
    );

  const creditCards = [
    {
      type: "massage" as const,
      balance: getDepartmentBalance("massage"),
    },
    {
      type: "beauty" as const,
      balance: getDepartmentBalance("beauty"),
    },
    {
      type: "pet" as const,
      balance: getDepartmentBalance("pet"),
    },
  ];

  return (
    <CreditAccountOverviewScreen
      overviewBalance={String(overviewBalance)}
      generalBalance={getBalance(CreditTypeEnum.General)}
      creditAccounts={creditCards}
    />
  );
};
